import { css, html, LitElement, nothing, type TemplateResult } from "lit";
import { SignalWatcher } from "@lit-labs/signals";
import { customElement } from "lit/decorators.js";
import { pipe, type Send } from "refrakt";
import { scope } from "refrakt/middleware/scope.js";
import * as Option from "@gordonb/result/option";
import * as Styles from "../../styles.ts";
import { type AppRouter, router } from "../../services/router.ts";
import * as Logger from "../../services/logger.ts";
import {
  type Action,
  activateCanvasViewKey,
  type AppStore,
  appStore,
  type CanvasViewKey,
  type Model,
} from "../app-store.ts";
import * as AiChat from "../app/ai-chat-store.ts";
import { createCancelGroup } from "../../util/cancel.ts";
import { on } from "../../util/dom.ts";
import { type Ulid } from "../../../models/ulid.ts";
import { type Slug } from "../../../models/slug.ts";
import * as Config from "../../client-config.ts";
import { assertNever } from "../../../util/never.ts";
import { type RequestDeleteNoteDetail } from "../force-edit-store.ts";
import { initAuthStateListener } from "../../services/supabase.ts";
import "../app/canvas/canvas-panel.ts";
import "../app/canvas/force-edit-panel.ts";
import "../app/ai-chat.ts";
import "../app/canvas/index-panel.ts";
import "../app/canvas/project-info-panel.ts";
import "../app/canvas/force-table-panel.ts";
import "../app/canvas/force-vis-panel.ts";
import "../app/canvas/force-map-panel.ts";
import "../app/canvas/report-panel.ts";
import "../app/canvas/scan-hits-panel.ts";
import "../dui/center/dui-center.ts";
import "../dui/sidebar/dui-sidebar-provider.ts";
import "../dui/sidebar/dui-sidebar-inset.ts";
import "./sidebar-view.ts";
import "./auth-view.ts";
import "./chat-view.ts";
import "./home-view.ts";
import "./library-view.ts";
import "./library-note-view.ts";
import "./project-view.ts";
import "../app/loading.ts";
import "./create-project-modal.ts";
import "../note-editor/note-edit-modal.ts";

const logger = Logger.create({ source: "client/app-view" });

export const configRouter = (router: AppRouter, store: AppStore): void => {
  router.on(Config.BASE_PATH, (_req, ctx) => {
    store.send({ type: "view-home", session: ctx });
  });

  router.on(Config.LIBRARY_URL, (_req, ctx) => {
    store.send({ type: "view-library", session: ctx });
  });

  router.on(Config.LIBRARY_NOTE_URL, (req, ctx) => {
    store.send({
      type: "view-library-note",
      slug: Option.unwrap(req.pathname.groups.slug),
      session: ctx,
    });
  });

  router.on(Config.PROJECT_URL, (req, ctx) => {
    store.send({
      type: "view-project",
      projectId: req.pathname.groups.project,
      session: ctx,
    });
  });

  router.on(Config.PROJECT_CONVERSATION_URL, (req, ctx, state) => {
    const routeState = state as { loadMessages?: boolean } | undefined;
    store.send({
      type: "view-project-conversation",
      projectId: Option.unwrap(req.pathname.groups.project),
      conversationId: Option.unwrap(req.pathname.groups.conversation),
      session: ctx,
      loadMessages: routeState?.loadMessages ?? true,
    });
  });

  router.notFound((route) => {
    logger.warn("Route not found", { route });
    // Just redirect to the root URL
    router.navigate(Config.BASE_PATH);
  });
};

const styles = css`
  :host {
    display: block;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
    background-color: var(--background);
    color: var(--foreground);
  }

  .app {
    height: 100%;
    animation: fade-in 1s ease-in-out forwards;
  }

  .attachment-chips {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2);
    margin-top: var(--space-2);
  }

  #dui-overlay-root {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 9999;
  }

  #dui-overlay-root > * {
    pointer-events: auto;
  }

  dui-sidebar-provider {
    --sidebar-width: var(--space-56);
    --sidebar-separator: transparent;
  }
`;

const activateCanvasViewAction = (viewId: string): Action | undefined => {
  const canvasView = activateCanvasViewKey(viewId);
  if (canvasView) {
    return { type: "set-canvas-view", canvasView, slug: undefined };
  }
  return undefined;
};

const projectConversationView = (
  state: Model,
  store: AppStore,
): TemplateResult => {
  const aiChatStore: AiChat.AiChatStore = pipe(
    store,
    scope(
      (s: Model) => s.aiChat,
      (action: AiChat.Action): Action => ({
        type: "ai-chat",
        action,
      }),
    ),
  );

  const onRequestDeleteConversation = (event: CustomEvent<Ulid>) => {
    store.send({ type: "delete-conversation", value: event.detail });
  };

  const onActivateForce = (event: CustomEvent<Ulid>): void => {
    store.send({
      type: "set-canvas-view",
      canvasView: "force-edit",
      slug: event.detail,
    });
  };

  const onActivateCanvasView = (event: CustomEvent<string>): void => {
    const action = activateCanvasViewAction(event.detail);
    if (action) store.send(action);
  };

  const onCanvasClose = () => {
    if (state.canvasView === "force-edit") {
      store.send({
        type: "set-canvas-view",
        canvasView: "force-table",
        slug: undefined,
      });
    } else {
      store.send({
        type: "set-canvas-view",
        canvasView: undefined,
        slug: undefined,
      });
    }
  };

  const onCanvasViewChange = (event: CustomEvent<CanvasViewKey>) => {
    store.send({
      type: "set-canvas-view",
      canvasView: event.detail,
      slug: undefined,
    });
  };

  const onToggleCanvas = () => {
    if (state.canvasView !== undefined) {
      onCanvasClose();
    } else {
      store.send({
        type: "set-canvas-view",
        canvasView: state.lastCanvasView ?? "project-info",
        slug: state.lastCanvasSlug,
      });
    }
  };

  return html`
    <chat-view
      ?show-canvas="${state.canvasView !== undefined}"
      @canvas-close="${onCanvasClose}"
      @activate-canvas-view="${onActivateCanvasView}"
      @activate-force="${onActivateForce}"
      @request-delete-conversation="${onRequestDeleteConversation}"
      @toggle-canvas="${onToggleCanvas}"
    >
      <ai-chat
        .store="${aiChatStore}"
        .accessToken="${store.get().session?.access_token}"
        .projectTitle="${state.projectTitle}"
        .projectId="${state.projectId}"
        .scenarioPhase="${state.scenarioPhase}"
        ?canvas-open="${state.canvasView !== undefined}"
      >
      </ai-chat>
      <div slot="canvas" @canvas-view-change="${onCanvasViewChange}">
        ${canvasPanel(state, store)}
      </div>
    </chat-view>
  `;
};

const projectView = (store: AppStore): TemplateResult => {
  return html`<project-view .store="${store}"></project-view>`;
};

const projectInfoView = (
  projectTitle: string,
  projectFocus: string,
  send: Send<Action>,
): TemplateResult => {
  const onTitleInput = (event: CustomEvent<string>) => {
    send({ type: "set-project-title", value: event.detail });
  };

  const onQuestionInput = (event: CustomEvent<string>) => {
    send({ type: "set-project-focus", value: event.detail });
  };

  return html`
    <project-info-panel
      .projectTitle="${projectTitle}"
      .focalQuestion="${projectFocus}"
      @project-title-input="${onTitleInput}"
      @focal-question-input="${onQuestionInput}"
    >
    </project-info-panel>
  `;
};

const projectsView = (_store: AppStore): TemplateResult => {
  return html` TODO: Projects `;
};

const forceTableView = (store: AppStore): TemplateResult => {
  const state = store.get();
  const heading = `Forces (${state.forces.size})`;
  return html`
    <force-table-panel
      .heading="${heading}"
      .forces="${state.forces}"
      .projectId="${state.projectId}"
      .send="${store.send}"
    >
    </force-table-panel>
  `;
};

const forceVisView = (store: AppStore): TemplateResult => {
  const state = store.get();
  const heading = `Connect Forces (${state.forces.size})`;
  return html`
    <force-vis-panel
      .heading="${heading}"
      .forces="${state.forces}"
      .forceConnections="${state.forceConnections}"
      .project="${state.projectId}"
      .send="${store.send}"
    >
    </force-vis-panel>
  `;
};

const forceMapView = (state: Model, send: Send<Action>): TemplateResult => {
  return html`
    <force-map-panel .forces="${state.forces}" .send="${send}">
    </force-map-panel>
  `;
};

const forceEditView = (store: AppStore): TemplateResult => {
  const state = store.get();
  const accessToken = state.session?.access_token;

  const onSaveSuccess = (event: CustomEvent<Slug>) => {
    store.send({ type: "force-edit-save-success", value: event.detail });
  };

  const onRequestDeleteNote = ({
    detail,
  }: CustomEvent<RequestDeleteNoteDetail>) => {
    if (state.projectId) {
      store.send({
        type: "delete-note",
        slug: detail.slug,
        projectId: state.projectId,
        redirectTo: detail.redirectTo,
        accessToken,
      });
    }
  };

  return html`
    <force-edit-panel
      .slug="${state.slug}"
      .projectId="${state.projectId}"
      .accessToken="${accessToken}"
      @save-success="${onSaveSuccess}"
      @request-delete-note="${onRequestDeleteNote}"
    ></force-edit-panel>
  `;
};

const scanHitsView = (store: AppStore): TemplateResult => {
  const state = store.get();
  const onEditNote = (event: CustomEvent<{ slug: string }>) => {
    store.send({ type: "edit-note", slug: event.detail.slug });
  };
  return html`
    <scan-hits-panel
      .scanHits="${state.scanHits}"
      .projectId="${state.projectId}"
      @edit-note="${onEditNote}"
    ></scan-hits-panel>
  `;
};

const reportView = (store: AppStore): TemplateResult => {
  const state = store.get();
  return html`
    <report-panel
      .report="${state.report}"
      .send="${store.send}"
    ></report-panel>
  `;
};

const canvasContent = (
  state: Model,
  store: AppStore,
): TemplateResult | typeof nothing => {
  switch (state.canvasView) {
    case "project-info":
      return projectInfoView(
        state.projectTitle,
        state.projectFocus,
        store.send,
      );
    case "force-table":
      return forceTableView(store);
    case "force-vis":
      return forceVisView(store);
    case "force-map":
      return forceMapView(state, store.send);
    case "report":
      return reportView(store);
    case "scan-hits":
      return scanHitsView(store);
    default:
      return nothing;
  }
};

const canvasPanel = (
  state: Model,
  store: AppStore,
): TemplateResult | typeof nothing => {
  if (state.canvasView === undefined) return nothing;
  // force-edit keeps its own canvas-panel (needs toolbar mode + overlay slot)
  if (state.canvasView === "force-edit") return forceEditView(store);
  // All tabbed views share a persistent canvas-panel
  return html`
    <canvas-panel
      .canvasView="${state.canvasView}"
      ?inset="${state.canvasView !== "force-vis"}"
    >
      ${canvasContent(state, store)}
    </canvas-panel>
  `;
};

const mainView = (store: AppStore): TemplateResult => {
  const state = store.get();
  switch (state.view) {
    case "home":
      return html`<home-view></home-view>`;
    case "library":
      return html`<library-view .store="${store}"></library-view>`;
    case "library-note":
      return html`<library-note-view .store="${store}"></library-note-view>`;
    case "projects":
      return projectsView(store);
    case "project":
      return projectView(store);
    case "project-conversation":
      return projectConversationView(state, store);
    default:
      return assertNever(state.view, "Unknown view type");
  }
};

export const appLoggedInView = (store: AppStore): TemplateResult => {
  const state = store.get();

  const onSidebarChange = (e: CustomEvent<{ open: boolean }>) => {
    store.send({ type: "set-sidebar-open", value: e.detail.open });
  };

  return html`
    <create-project-modal .store="${store}"></create-project-modal>
    <note-edit-modal .store="${store}"> </note-edit-modal>
    <div class="app">
      <dui-sidebar-provider
        .open="${state.sidebarOpen}"
        @sidebar-open-change="${onSidebarChange}"
        collapsible="offcanvas"
      >
        <sidebar-view .store="${store}"></sidebar-view>
        <dui-sidebar-inset> ${mainView(store)} </dui-sidebar-inset>
      </dui-sidebar-provider>
    </div>
  `;
};

export const appLoggedOutView = (store: AppStore): TemplateResult => {
  const state = store.get();
  return html`
    <dui-center>
      <auth-view
        .error="${state.authError}"
        .isLoading="${state.loading}"
        .otpSent="${state.otpSent}"
        .send="${store.send}"
      ></auth-view>
    </dui-center>
  `;
};

export const appLoadingView = (): TemplateResult => {
  return html`<loading-view></loading-view>`;
};

@customElement("app-view")
export class AppElement extends SignalWatcher(LitElement) {
  static override styles = [
    ...Styles.tokens,
    Styles.base,
    Styles.legacyUtility,
    Styles.animations,
    styles,
  ];

  #cancel = createCancelGroup();

  override connectedCallback(): void {
    super.connectedCallback();

    configRouter(router, appStore);
    const cancelRouter = router.start();
    this.#cancel.add(cancelRouter);

    const onKeyup = on(window, "keyup", (event: Event) => {
      if ((event as KeyboardEvent).key === "Escape") {
        appStore.send({ type: "esc-key" });
      }
    });

    this.#cancel.add(onKeyup);

    const onSpaNavigate = on(this, "spa-navigate", (event: Event) => {
      const { href } = (event as CustomEvent<{ href: string }>).detail;
      router.navigate(href);
    });
    this.#cancel.add(onSpaNavigate);

    const cancelAuthListener = initAuthStateListener((session) => {
      appStore.send({ type: "set-authorized-user", value: session });
    });

    this.#cancel.add(cancelAuthListener);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.#cancel();
  }

  override render(): TemplateResult {
    const state = appStore.get();

    let viewContent: TemplateResult;

    // Show loading state while checking session
    if (state.loading) {
      viewContent = appLoadingView();
    } else if (!state.session) {
      // Show auth view if not authenticated
      viewContent = appLoggedOutView(appStore);
    } else {
      // Show main app if authenticated
      viewContent = appLoggedInView(appStore);
    }

    return html`
      ${viewContent}
      <div id="dui-overlay-root"></div>
    `;
  }
}
